import { type FC, type ReactNode, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { FormattedPrice } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';

import { useShowPricingGuidance } from './helpers/useShowPricingGuidance';
import { useGetEstimatedValueRange } from './helpers/useGetEstimatedValueRange';
import { useGetPriceAmountByType } from './pricingGuidanceHelpers';
import { trackPricingGuidanceRangeDisplay } from './pricingGuidanceTracking';

import { type EstimatedValue_item$key } from './__generated__/EstimatedValue_item.graphql';

type Props = {
    item: EstimatedValue_item$key;
    currency: string;
    textClassName: string;
    infoContent: ReactNode;
};

const EstimatedValue: FC<Props> = ({ item: itemRef, currency, textClassName, infoContent }) => {
    const item = useFragment(
        graphql`
            fragment EstimatedValue_item on Item {
                ...useShowPricingGuidance_item
                ...useGetEstimatedValueRange_item
                ...pricingGuidanceHelpers_item
                ...pricingGuidanceTracking_item
            }
        `,
        itemRef
    );

    const showPricingGuidance = useShowPricingGuidance({ item, currency });
    const { rangeMin, rangeMax } = useGetEstimatedValueRange({ item, currency });
    const itemPrice = useGetPriceAmountByType({ item, currency, type: 'ITEM_PRICE' });

    useEffect(() => {
        if (showPricingGuidance) {
            trackPricingGuidanceRangeDisplay({ item });
        }
    }, [showPricingGuidance, item]);

    if (!showPricingGuidance) {
        return null;
    }

    return (
        <div className={classnames(dibsCss.flex, dibsCss.itemsCenter)}>
            <span className={classnames(dibsCss.mrXxsmall, dibsCss.textSatan, textClassName)}>
                <FormattedMessage
                    id="EstimatedValue.estimate"
                    defaultMessage="1stDibs Estimate: <range>{rangeMin} - {rangeMax}</range>"
                    values={{
                        rangeMin: (
                            <FormattedPrice key="rangeMin" value={rangeMin} currency={currency} />
                        ),
                        rangeMax: (
                            <FormattedPrice key="rangeMax" value={rangeMax} currency={currency} />
                        ),
                        range: content => (
                            <span
                                className={
                                    itemPrice <= rangeMax ? dibsCss.textSuccesssecondary : undefined
                                }
                            >
                                {content}
                            </span>
                        ),
                    }}
                />
            </span>
            {infoContent}
        </div>
    );
};

export default EstimatedValue;
